/**
 * Authentication Bridge for iFrame Communication
 *
 * Handles secure token exchange between the parent app and Compass
 * when running embedded as an iframe.
 *
 * Security considerations:
 * - Tokens are stored in memory only (NOT localStorage)
 * - Auto-refresh logic requests new tokens before expiry
 * - Message origin validation for production use
 */

import {
  isEmbedded,
  sendToParent,
  type IFrameMessage,
  type AuthTokenPayload,
} from './types';

// Token refresh buffer - request new token 60 seconds before expiry
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;

// Minimum token lifetime to prevent rapid refresh loops
const MIN_TOKEN_LIFETIME_MS = 5 * 1000;

// ============================================================================
// Internal State (memory only for security)
// ============================================================================

let currentToken: string | null = null;
let tokenExpiresAt: number | null = null;
let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
let messageHandler: ((event: MessageEvent) => void) | null = null;
let tokenListeners: Set<(token: string | null) => void> = new Set();
let isInitialized = false;

// ============================================================================
// Token Management
// ============================================================================

/**
 * Get the current auth token
 *
 * @returns The current token or null if not authenticated
 */
export const getAuthToken = (): string | null => {
  // Check if token has expired
  if (currentToken && tokenExpiresAt && Date.now() >= tokenExpiresAt) {
    console.debug('[auth-bridge] Token expired, clearing');
    clearToken();
    // Request a fresh token
    requestToken();
    return null;
  }

  return currentToken;
};

/**
 * Check if the user is currently authenticated
 *
 * @returns true if a valid token exists
 */
export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  return token !== null;
};

/**
 * Set the auth token (called when receiving from parent)
 *
 * @param payload - Token payload from parent
 */
const setToken = (payload: AuthTokenPayload): void => {
  currentToken = payload.token;
  tokenExpiresAt = payload.expiresAt ?? null;

  console.debug('[auth-bridge] Token received', {
    hasToken: !!currentToken,
    expiresAt: tokenExpiresAt ? new Date(tokenExpiresAt).toISOString() : null,
  });

  // Notify listeners
  notifyListeners(currentToken);

  // Schedule auto-refresh if expiry is known
  scheduleTokenRefresh();
};

/**
 * Clear the current token
 */
const clearToken = (): void => {
  currentToken = null;
  tokenExpiresAt = null;

  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }

  notifyListeners(null);
};

/**
 * Notify all token listeners of a change
 */
const notifyListeners = (token: string | null): void => {
  tokenListeners.forEach((listener) => {
    try {
      listener(token);
    } catch (error) {
      console.error('[auth-bridge] Token listener error:', error);
    }
  });
};

/**
 * Subscribe to token changes
 *
 * @param listener - Callback when token changes
 * @returns Unsubscribe function
 */
export const subscribeToToken = (
  listener: (token: string | null) => void
): (() => void) => {
  tokenListeners.add(listener);

  // Immediately call with current token
  listener(currentToken);

  return () => {
    tokenListeners.delete(listener);
  };
};

// ============================================================================
// Token Refresh Logic
// ============================================================================

/**
 * Schedule automatic token refresh before expiry
 */
const scheduleTokenRefresh = (): void => {
  // Clear any existing refresh timer
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }

  // Can't schedule refresh without expiry time
  if (!tokenExpiresAt) {
    return;
  }

  const now = Date.now();
  const timeUntilExpiry = tokenExpiresAt - now;
  const timeUntilRefresh = timeUntilExpiry - TOKEN_REFRESH_BUFFER_MS;

  // Don't schedule if token is already expired or about to expire
  if (timeUntilRefresh < MIN_TOKEN_LIFETIME_MS) {
    console.debug('[auth-bridge] Token expiring soon, requesting refresh now');
    requestToken();
    return;
  }

  console.debug('[auth-bridge] Scheduling token refresh in', timeUntilRefresh, 'ms');

  refreshTimeoutId = setTimeout(() => {
    console.debug('[auth-bridge] Auto-refreshing token');
    requestToken();
  }, timeUntilRefresh);
};

// ============================================================================
// Parent Communication
// ============================================================================

/**
 * Request a fresh token from the parent
 */
export const requestToken = (): void => {
  if (!isEmbedded()) {
    console.debug('[auth-bridge] Not embedded, cannot request token');
    return;
  }

  console.debug('[auth-bridge] Requesting token from parent');
  sendToParent({ type: 'REQUEST_TOKEN' });
};

/**
 * Handle incoming messages from parent
 */
const handleMessage = (event: MessageEvent): void => {
  // In production, validate event.origin against allowed origins
  // For now, accept all origins for development flexibility
  const message = event.data as IFrameMessage;

  if (!message || typeof message !== 'object' || !message.type) {
    return;
  }

  switch (message.type) {
    case 'AUTH_TOKEN': {
      const payload = message.payload as AuthTokenPayload | undefined;
      if (payload?.token) {
        setToken(payload);
      } else {
        console.warn('[auth-bridge] Received AUTH_TOKEN without valid payload');
        clearToken();
      }
      break;
    }

    // Other message types can be handled by other bridges
    default:
      break;
  }
};

// ============================================================================
// Lifecycle
// ============================================================================

/**
 * Initialize the auth bridge
 *
 * Sets up message listeners and requests initial token.
 * Safe to call multiple times - will only initialize once.
 */
export const initAuthBridge = (): void => {
  if (isInitialized) {
    console.debug('[auth-bridge] Already initialized');
    return;
  }

  if (!isEmbedded()) {
    console.debug('[auth-bridge] Not embedded, skipping initialization');
    isInitialized = true;
    return;
  }

  console.debug('[auth-bridge] Initializing');

  // Set up message listener
  messageHandler = handleMessage;
  window.addEventListener('message', messageHandler);

  // Request initial token
  requestToken();

  isInitialized = true;
};

/**
 * Cleanup the auth bridge
 *
 * Removes message listeners and clears tokens.
 */
export const destroyAuthBridge = (): void => {
  if (messageHandler) {
    window.removeEventListener('message', messageHandler);
    messageHandler = null;
  }

  clearToken();
  tokenListeners.clear();
  isInitialized = false;

  console.debug('[auth-bridge] Destroyed');
};

/**
 * Check if the auth bridge has been initialized
 */
export const isAuthBridgeInitialized = (): boolean => {
  return isInitialized;
};
